import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { audiences, Audience } from '../../config/brand';
import { useAudience } from './AudienceContext';
import './AudienceSwitch.css';

type Props = {
  /** Announced to screen readers — each instance needs its own. */
  label: string;
  id: string;
};

/**
 * iOS-style segmented control. The thumb is one absolutely positioned pill
 * driven by `--seg-x` / `--seg-w`, measured from the selected button, so it
 * slides between options on a spring instead of cross-fading.
 */
const AudienceSwitch: React.FC<Props> = ({ label, id }) => {
  const { audience, setAudience } = useAudience();
  const trackRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  const position = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const active = track.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!active) return;
    track.style.setProperty('--seg-x', `${active.offsetLeft}px`);
    track.style.setProperty('--seg-w', `${active.offsetWidth}px`);
    setReady(true);
  }, []);

  useLayoutEffect(position, [audience, position]);

  // Re-measure when the track resizes or the display font finally lands —
  // both change option widths after the first paint.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(position);
    observer.observe(track);
    document.fonts?.ready.then(position).catch(() => {});
    return () => observer.disconnect();
  }, [position]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const order = audiences.map((a) => a.id);
    const i = order.indexOf(audience);
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      setAudience(order[(i + 1) % order.length] as Audience);
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAudience(order[(i - 1 + order.length) % order.length] as Audience);
    }
  };

  return (
    <div
      className={`seg audience-switch${ready ? ' seg--ready' : ''}`}
      role="tablist"
      aria-label={label}
      ref={trackRef}
      onKeyDown={onKeyDown}
    >
      <span className="seg__thumb" aria-hidden="true" />
      {audiences.map((option) => (
        <button
          key={option.id}
          type="button"
          role="tab"
          id={`${id}-${option.id}`}
          className="seg__option"
          aria-selected={audience === option.id}
          tabIndex={audience === option.id ? 0 : -1}
          onClick={() => setAudience(option.id)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export default AudienceSwitch;
